import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtRelative, PRIORITY_LABELS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LayoutGrid, List, Search, X, ExternalLink, Wrench } from "lucide-react";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { useRealtimeEvent } from "@/lib/realtime-context";
import { STATUS_PILL, shipmentLabel } from "@/pages/returns/shipmentStatusMap";

const PRIO_COLOR = {
  low: "text-muted-foreground",
  medium: "text-blue-500",
  high: "text-amber-500",
  urgent: "text-red-500",
};

function MiniShipmentBadge({ shipment, direction }) {
  if (!shipment || shipment.status === "cancelled") return null;
  const pill = STATUS_PILL[shipment.status] || STATUS_PILL.created;
  return (
    <Badge variant="outline" className={`gap-1 text-[10px] px-1.5 py-0 h-5 w-fit ${pill.color}`} data-testid={`service-parcel-${direction}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${pill.dot}`} />
      {direction === "inbound" ? "↓" : "↑"} {shipmentLabel(shipment.status, direction)}
    </Badge>
  );
}

function ParcelsCell({ shipments }) {
  const inb = shipments?.inbound;
  const out = shipments?.outbound;
  const hasAny = (inb && inb.status !== "cancelled") || (out && out.status !== "cancelled");
  if (!hasAny) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex flex-col gap-1">
      <MiniShipmentBadge shipment={inb} direction="inbound" />
      <MiniShipmentBadge shipment={out} direction="outbound" />
    </div>
  );
}

export default function ServiceListPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [problemTypes, setProblemTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [r, s, p] = await Promise.all([
        api.get("/service"),
        api.get("/service/statuses"),
        api.get("/service/problem-types"),
      ]);
      setItems(r.data);
      setStatuses(s.data);
      setProblemTypes(p.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const debouncedReload = useCallback(() => {
    if (debouncedReload._t) clearTimeout(debouncedReload._t);
    debouncedReload._t = setTimeout(() => { load(); }, 400);
  }, []);
  useRealtimeEvent("service.created", debouncedReload);
  useRealtimeEvent("service.updated", debouncedReload);

  const statusMap = useMemo(() => Object.fromEntries(statuses.map((s) => [s.id, s])), [statuses]);
  const ptMap = useMemo(() => Object.fromEntries(problemTypes.map((p) => [p.id, p])), [problemTypes]);

  const filtered = items.filter((r) => {
    if (statusFilter !== "all" && r.status_id !== statusFilter) return false;
    if (priorityFilter !== "all" && r.priority !== priorityFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        r.service_number.toLowerCase().includes(q) ||
        r.order_number.toLowerCase().includes(q) ||
        r.customer_name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.phone.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const clearFilters = () => { setSearch(""); setStatusFilter("all"); setPriorityFilter("all"); };
  const hasFilters = search || statusFilter !== "all" || priorityFilter !== "all";

  return (
    <div className="space-y-6" data-testid="service-list-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Modul · Service & Garanții</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Cereri service</h1>
          <p className="text-muted-foreground text-sm mt-1">{filtered.length} {filtered.length === 1 ? "rezultat" : "rezultate"}</p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value="list">
            <TabsList>
              <TabsTrigger value="list"><List className="h-4 w-4 mr-1.5" /> Listă</TabsTrigger>
              <TabsTrigger value="kanban" onClick={() => navigate("/service/kanban")}>
                <LayoutGrid className="h-4 w-4 mr-1.5" /> Kanban
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Link to="/service/public" target="_blank">
            <Button variant="outline" size="sm" data-testid="open-public-service-form">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Formular public
            </Button>
          </Link>
        </div>
      </div>

      <Card className="p-4 bg-card border-border">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Caută după număr, client, email, telefon…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
              data-testid="service-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[180px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate statusurile</SelectItem>
              {statuses.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="h-9 w-[160px]"><SelectValue placeholder="Prioritate" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate prioritățile</SelectItem>
              {Object.entries(PRIORITY_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
            </SelectContent>
          </Select>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-3.5 w-3.5 mr-1.5" /> Reset filtre
            </Button>
          )}
        </div>
      </Card>

      <Card className="bg-card border-border overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 border-b border-border">
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                <th className="px-4 py-3">Cerere</th>
                <th className="px-4 py-3">Comandă</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Telefon</th>
                <th className="px-4 py-3">Problemă</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Prioritate</th>
                <th className="px-4 py-3 text-right">Creat</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}><td colSpan={9} className="px-4 py-3"><Skeleton className="h-6 w-full" /></td></tr>
                  ))
                : filtered.length === 0
                ? (
                  <tr><td colSpan={9} className="px-4 py-16 text-center text-muted-foreground">
                    <Wrench className="h-6 w-6 mx-auto mb-3 text-muted-foreground/40" />
                    <div className="mb-2">Nicio cerere service.</div>
                    <Link to="/service/public" target="_blank" className="text-primary text-xs underline">Trimite o cerere de test</Link>
                  </td></tr>
                )
                : filtered.map((r) => {
                    const s = statusMap[r.status_id];
                    const pt = ptMap[r.problem_type_id];
                    return (
                      <tr
                        key={r.id}
                        className="hover:bg-secondary/40 cursor-pointer transition-colors"
                        onClick={() => navigate(`/service/${r.id}`)}
                        data-testid={`service-row-${r.service_number}`}
                      >
                        <td className="px-4 py-3 font-mono text-xs font-medium">{r.service_number}</td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">#{r.order_number}</td>
                        <td className="px-4 py-3 font-medium">{r.customer_name}</td>
                        <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{r.phone}</td>
                        <td className="px-4 py-3 text-xs">{pt?.name || "—"}</td>
                        <td className="px-4 py-3">
                          {s && (
                            <Badge variant="outline" style={{ color: s.color, background: s.color + "15", borderColor: s.color + "30" }}>
                              {s.name}
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <ParcelsCell shipments={r.shipments} />
                        </td>
                        <td className={`px-4 py-3 text-xs font-medium ${PRIO_COLOR[r.priority] || "text-muted-foreground"}`}>
                          {PRIORITY_LABELS[r.priority] || "—"}
                        </td>
                        <td className="px-4 py-3 text-right text-muted-foreground text-xs">{fmtRelative(r.created_at)}</td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
